import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Expandable,
  Flex,
  FlexItem,
  Loading,
  P,
  Table,
} from "orcs-design-system";
import { useQuery } from "@apollo/client";
import { capitalize } from "lodash";
import icons from "src/config/icons";

import ErrorNotification from "src/components/ErrorNotification";
import {
  getIntegrationType,
  SYNC,
} from "src/components/DatasourceDetailPage/utils/getIntegrationType";
import {
  getTransaction,
  getTransactionEffects,
} from "./ImportTransactionDetails.graphql";

const PAGE_LIMIT = 2000;

const EmailNotificationDetails = ({ metrics }) => {
  if (!metrics) {
    return null;
  }

  const {
    dryRun,
    recipientsEvaluated,
    emailsWouldSend,
    emailsSent,
    emailsSkipped,
    emailsFailed,
    rowsProduced,
    recipientManagerIds,
  } = metrics;

  return (
    <ul>
      <li>Dry run: {dryRun ? "Yes" : "No"}</li>
      <li>Recipients evaluated: {recipientsEvaluated ?? 0}</li>
      <li>Emails would send: {emailsWouldSend ?? 0}</li>
      <li>Emails sent: {emailsSent ?? 0}</li>
      <li>Emails skipped: {emailsSkipped ?? 0}</li>
      <li>Emails failed: {emailsFailed ?? 0}</li>
      <li>Rows produced: {rowsProduced ?? 0}</li>
      {recipientManagerIds && recipientManagerIds.length > 0 && (
        <li>{`Recipient manager IDs (${
          recipientManagerIds.length
        }): ${recipientManagerIds.join(", ")}`}</li>
      )}
    </ul>
  );
};

EmailNotificationDetails.propTypes = {
  metrics: PropTypes.shape({
    dryRun: PropTypes.bool,
    recipientsEvaluated: PropTypes.number,
    emailsWouldSend: PropTypes.number,
    emailsSent: PropTypes.number,
    emailsSkipped: PropTypes.number,
    emailsFailed: PropTypes.number,
    rowsProduced: PropTypes.number,
    recipientManagerIds: PropTypes.arrayOf(PropTypes.string),
  }),
};

const useInView = (options = {}) => {
  const { triggerOnce = false } = options;
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  const setRef = useCallback((node) => {
    ref.current = node;
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else if (!triggerOnce) {
          setInView(false);
        }
      },
      { threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [triggerOnce]);

  return { ref: setRef, inView };
};

const EntityTypeDetails = ({ transactionId, entityType }) => {
  const [changes, setChanges] = React.useState([]);
  const [pageCount, setPageCount] = React.useState(0);
  const [pagesLoading, setPagesLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const { ref, inView } = useInView({
    /* Optional options */
    triggerOnce: true,
  });

  const { error } = useQuery(getTransactionEffects, {
    skip: !inView,
    variables: {
      transactionId,
      entityType,
      offset: pageCount * PAGE_LIMIT,
      limit: PAGE_LIMIT,
      includeTotal: pageCount === 0,
    },
    onCompleted: (data) => {
      setChanges([...changes, ...data.result.changes]);
      if (data.result.total) {
        setTotal(data.result.total);
      }
      if (data.result.changes.length === PAGE_LIMIT) {
        setPageCount(pageCount + 1);
      } else {
        setPagesLoading(false);
      }
    },
  });

  const columns = [
    ...(entityType === "group" || entityType === "person"
      ? [
          {
            header: "Id",
            accessorKey: "entityId",
          },
        ]
      : /** membershipId = personId + groupId */ [
          { header: "Person Id", accessorKey: "entityId.personId" },
          { header: "Group Id", accessorKey: "entityId.groupId" },
        ]),
    {
      header: "Attribute",
      accessorKey: "attributeType",
    },
    {
      header: "Value",
      accessorKey: "value",
      accessorFn: (row) => {
        if (row.value === null) {
          return null;
        }

        if (typeof row.value === "string") {
          return row.value;
        }
        return JSON.stringify(row.value);
      },
    },
    ...(entityType === "group" || entityType === "person"
      ? [
          {
            header: "Existing",
            accessorKey: "existingValue",
            accessorFn: (row) => {
              if (row.existingValue === null) {
                return null;
              }

              if (typeof row.existingValue === "string") {
                return row.existingValue;
              }
              return JSON.stringify(row.existingValue);
            },
          },
        ]
      : []),
  ];

  return (
    <div ref={ref}>
      {pagesLoading && (
        <Flex alignItems="center" gap="s">
          <Loading />
          {total > 0 && (
            <P>
              {changes.length} of {total} attributes
            </P>
          )}
        </Flex>
      )}
      {error && (
        <ErrorNotification
          message={
            error?.message ?? "Unknown error loading transaction group details"
          }
          report={true}
          floating={false}
        />
      )}
      {!error && changes && (
        <Table
          data={changes}
          columns={columns}
          enableRowVirtualization={true}
          rowVirtualizerOptions={{
            overscan: 5, // adjust the number or rows that are rendered above and below the visible area of the table
          }}
          enablePagination={false}
        />
      )}
    </div>
  );
};

EntityTypeDetails.propTypes = {
  transactionId: PropTypes.string.isRequired,
  entityType: PropTypes.string.isRequired,
};

const TransactionDetails = ({
  transaction,
  importId,
  datasourceType,
  emailNotificationMetrics,
}) => {
  const integrationType = getIntegrationType(datasourceType);
  const integrationTypeDisplayName = capitalize(integrationType);
  if (!transaction) {
    return (
      <>
        <ul>
          <li>{`${integrationTypeDisplayName} ID: ${importId}`}</li>
          <li>No changes detected</li>
        </ul>
        <EmailNotificationDetails metrics={emailNotificationMetrics} />
      </>
    );
  }

  return (
    <Flex flexDirection="column" alignItems="stretch">
      <ul>
        <li>{`${integrationTypeDisplayName}/Transaction ID: ${transaction.transactionId}`}</li>
        <li>{transaction.isReverted ? "Not in effect" : "In effect"}</li>
        <li>Effective Date: {transaction.pit}</li>
        <li>{`${integrationTypeDisplayName} Date: ${transaction.timestamp}`}</li>
      </ul>
      <EmailNotificationDetails metrics={emailNotificationMetrics} />
      <FlexItem>
        <Expandable
          title={`${transaction.effects?.groups?.total} Groups`}
          small={true}
        >
          <EntityTypeDetails
            entityType="group"
            transactionId={transaction.transactionId}
          />
        </Expandable>
      </FlexItem>
      {integrationType !== SYNC && (
        <>
          <FlexItem>
            <Expandable
              title={
                datasourceType === "agents"
                  ? `${transaction.effects?.people?.total} Agents`
                  : `${transaction.effects?.people?.total} People`
              }
              small
            >
              <EntityTypeDetails
                entityType="person"
                transactionId={transaction.transactionId}
              />
            </Expandable>
          </FlexItem>
          <FlexItem>
            <Expandable
              title={`${transaction.effects?.memberships?.total} Memberships`}
              small
            >
              <EntityTypeDetails
                entityType="membership"
                transactionId={transaction.transactionId}
              />
            </Expandable>
          </FlexItem>
        </>
      )}
    </Flex>
  );
};

TransactionDetails.propTypes = {
  transaction: PropTypes.object,
  importId: PropTypes.string.isRequired,
  datasourceType: PropTypes.string,
  emailNotificationMetrics: PropTypes.object,
};

const ImportTransactionDetails = ({ importId, datasourceType, dataImport }) => {
  const { ref, inView } = useInView({
    /* Optional options */
    triggerOnce: true,
  });

  const { data, loading, error } = useQuery(getTransaction, {
    skip: !inView,
    variables: {
      transactionId: importId,
    },
  });

  if (loading) {
    return <Loading />;
  }

  const transaction = data?.result?.transactions?.[0];
  const emailNotificationMetrics = dataImport?.metrics?.emailNotifications;
  return (
    <div ref={ref}>
      <Card
        alternate
        title="Details"
        boxBorder="default"
        icon={icons.infoCircle}
        colour="success"
      >
        {loading && <Loading />}
        {!loading && !error && (
          <TransactionDetails
            transaction={transaction}
            importId={importId}
            datasourceType={datasourceType}
            emailNotificationMetrics={emailNotificationMetrics}
          />
        )}
        {error && (
          <ErrorNotification
            message={
              error?.message ?? "Unknown error loading transaction details"
            }
            report={true}
            floating={false}
          />
        )}
      </Card>
    </div>
  );
};

ImportTransactionDetails.propTypes = {
  importId: PropTypes.string.isRequired,
  datasourceType: PropTypes.string,
  dataImport: PropTypes.object,
};

export default ImportTransactionDetails;
